import { supportedLanguages, type SupportedLanguage } from "../i18n";
import { phoneRegion } from "./formattingLocale";

/** Languages a device can be set to display: the app's own, minus the key-echo pseudo-language. */
export type DeviceLanguage = Exclude<SupportedLanguage, 'dev'>;
export const DEVICE_LANGUAGES = supportedLanguages.filter((lng): lng is DeviceLanguage => lng !== 'dev');

/**
 * Region paired with each language when the phone doesn't supply one, so the
 * tag sent to the device always carries regional conventions.
 */
const HOME_REGIONS: Record<DeviceLanguage, string> = {
    en: 'GB',
    de: 'DE',
    fr: 'FR',
    nl: 'NL',
    pl: 'PL',
    sv: 'SE',
};

/** The app language a device locale tag maps to, or undefined for tags the app doesn't speak. */
export function deviceLanguageOf(locale: string | null | undefined): DeviceLanguage | undefined {
    const base = locale?.split('-')[0].toLowerCase();
    return DEVICE_LANGUAGES.find((lng) => lng === base);
}

/**
 * Full BCP 47 tag to store on a device for `language`: the same recipe as
 * formattingLocales() — the phone's region grafted onto the chosen language —
 * so the device formats dates the way the app does, with the language's home
 * region as the fallback (en → en-GB, sv → sv-SE, …).
 */
export function deviceLocaleForLanguage(language: DeviceLanguage): string {
    return `${language}-${phoneRegion() ?? HOME_REGIONS[language]}`;
}
